import React, { useState, useEffect } from 'react';

export const CreatableSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select or type...',
  isDisabled = false,
  className = '',
  formatCreateLabel,
  createOptionPosition = 'last',
  noOptionsMessage,
}: {
  value?: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
  formatCreateLabel?: (inputValue: string) => string;
  createOptionPosition?: 'first' | 'last';
  noOptionsMessage?: () => string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState(options);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Don't call onChange here - only when option is selected
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      handleOptionSelect(filteredOptions[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option: { label: string; value: string }) => {
    setInputValue(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (!isDisabled) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow option selection
    setTimeout(() => {
      setIsOpen(false);
      // If user typed something but didn't select an option,
      // and it matches an existing option exactly, auto-select it
      if (inputValue) {
        const exactMatch = options.find(
          (option) => option.label.toLowerCase() === inputValue.toLowerCase()
        );
        if (exactMatch && exactMatch.value !== value) {
          onChange(exactMatch.value);
        }
      }
    }, 150);
  };

  const showCreateOption =
    inputValue &&
    !options.some(
      (option) => option.value.toLowerCase() === inputValue.toLowerCase()
    );

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={isDisabled}
        className="flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 px-3 py-2 text-sm text-title-page-color-300 dark:text-gray-300 placeholder:text-title-page-color-300 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
      />

      {isOpen && (filteredOptions.length > 0 || showCreateOption) && (
        <div className="absolute z-50 w-full mt-1 bg-card-color-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Show create option first if createOptionPosition is 'first' */}
          {showCreateOption && createOptionPosition === 'first' && (
            <div
              className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer border-b border-gray-200 dark:border-gray-600"
              onMouseDown={() =>
                handleOptionSelect({ label: inputValue, value: inputValue })
              }
            >
              {formatCreateLabel ? formatCreateLabel(inputValue) : `Create "${inputValue}"`}
            </div>
          )}
          
          {/* Existing options */}
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-3 py-2 text-sm text-title-page-color-300 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer"
              onMouseDown={() => handleOptionSelect(option)}
            >
              {option.label}
            </div>
          ))}
          
          {/* Show create option last if createOptionPosition is 'last' */}
          {showCreateOption && createOptionPosition === 'last' && (
            <div
              className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer border-t border-gray-200 dark:border-gray-600"
              onMouseDown={() =>
                handleOptionSelect({ label: inputValue, value: inputValue })
              }
            >
              {formatCreateLabel ? formatCreateLabel(inputValue) : `Create "${inputValue}"`}
            </div>
          )}
          
          {/* No options message */}
          {filteredOptions.length === 0 && !showCreateOption && noOptionsMessage && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {noOptionsMessage()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
